
import { useMemo } from 'react';
import { FilterOption } from '../TableFilters';

interface VoltageRegulator {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    inputVoltage: number;
    outputVoltage: number;
    current: number;
    frequency: number;
    regulationAccuracy: number;
    responseTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

export const useVoltageRegulatorFilters = (
  regulators: VoltageRegulator[],
  activeFilters: Record<string, string[]>
) => {
  // Generate filter options based on available data
  const filterCategories = useMemo(() => {
    // Get unique locations
    const locations = [...new Set(regulators.map(reg => reg.location))].map(location => ({
      value: location,
      label: location,
      count: regulators.filter(reg => reg.location === location).length
    }));

    // Status options
    const statuses = [...new Set(regulators.map(reg => reg.status))].map(status => ({
      value: status,
      label: status,
      count: regulators.filter(reg => reg.status === status).length
    }));

    // Battery level ranges
    const batteryLevels: FilterOption[] = [
      { value: 'high', label: 'High (70-100%)', count: regulators.filter(r => r.batteryLevel > 70).length },
      { value: 'medium', label: 'Medium (30-70%)', count: regulators.filter(r => r.batteryLevel > 30 && r.batteryLevel <= 70).length },
      { value: 'low', label: 'Low (0-30%)', count: regulators.filter(r => r.batteryLevel <= 30).length }
    ];

    // Signal strength ranges
    const signalStrengths: FilterOption[] = [
      { value: 'strong', label: 'Strong (70-100%)', count: regulators.filter(r => r.signalStrength > 70).length },
      { value: 'moderate', label: 'Moderate (30-70%)', count: regulators.filter(r => r.signalStrength > 30 && r.signalStrength <= 70).length },
      { value: 'weak', label: 'Weak (0-30%)', count: regulators.filter(r => r.signalStrength <= 30).length }
    ];

    // Regulation accuracy ranges
    const regulationAccuracy: FilterOption[] = [
      { value: 'excellent', label: 'Excellent (>95%)', count: regulators.filter(r => r.readings.regulationAccuracy > 95).length },
      { value: 'good', label: 'Good (90-95%)', count: regulators.filter(r => r.readings.regulationAccuracy > 90 && r.readings.regulationAccuracy <= 95).length },
      { value: 'poor', label: 'Poor (<90%)', count: regulators.filter(r => r.readings.regulationAccuracy <= 90).length }
    ];

    return [
      { name: 'Location', options: locations },
      { name: 'Status', options: statuses },
      { name: 'BatteryLevel', options: batteryLevels },
      { name: 'SignalStrength', options: signalStrengths },
      { name: 'Regulation', options: regulationAccuracy }
    ];
  }, [regulators]);

  // Apply filters to the data
  const filteredRegulators = useMemo(() => {
    return regulators.filter(regulator => {
      // Check location filter
      if (activeFilters.Location.length > 0 && !activeFilters.Location.includes(regulator.location)) {
        return false;
      }
      
      // Check status filter
      if (activeFilters.Status.length > 0 && !activeFilters.Status.includes(regulator.status)) {
        return false;
      }
      
      // Check battery level filter
      if (activeFilters.BatteryLevel.length > 0) {
        const batteryMatch = activeFilters.BatteryLevel.some(level => {
          if (level === 'high') return regulator.batteryLevel > 70;
          if (level === 'medium') return regulator.batteryLevel > 30 && regulator.batteryLevel <= 70;
          if (level === 'low') return regulator.batteryLevel <= 30;
          return false;
        });
        if (!batteryMatch) return false;
      }
      
      // Check signal strength filter
      if (activeFilters.SignalStrength.length > 0) {
        const signalMatch = activeFilters.SignalStrength.some(signal => {
          if (signal === 'strong') return regulator.signalStrength > 70;
          if (signal === 'moderate') return regulator.signalStrength > 30 && regulator.signalStrength <= 70;
          if (signal === 'weak') return regulator.signalStrength <= 30;
          return false;
        });
        if (!signalMatch) return false;
      }
      
      // Check regulation accuracy filter
      if (activeFilters.Regulation.length > 0) {
        const regulationMatch = activeFilters.Regulation.some(reg => {
          if (reg === 'excellent') return regulator.readings.regulationAccuracy > 95;
          if (reg === 'good') return regulator.readings.regulationAccuracy > 90 && regulator.readings.regulationAccuracy <= 95;
          if (reg === 'poor') return regulator.readings.regulationAccuracy <= 90;
          return false;
        });
        if (!regulationMatch) return false;
      }
      
      return true;
    });
  }, [regulators, activeFilters]);

  return { filterCategories, filteredRegulators };
};
